<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { centerCropCloudinaryUrl } from "../utils/cloudinary.js";

const TILE_COUNT = 5;
const AUTOPLAY_DELAY = 5500;

const products = ref([]);
const isLoading = ref(true);
const error = ref("");
const currentSet = ref(0);
const isPaused = ref(false);
let autoplayTimer;

const productSets = computed(() => {
  const sets = [];

  for (let index = 0; index < products.value.length; index += TILE_COUNT) {
    sets.push(products.value.slice(index, index + TILE_COUNT));
  }

  if (sets.length > 1 && sets[sets.length - 1].length < 3) {
    sets.pop();
  }

  return sets;
});

const visibleProducts = computed(() => productSets.value[currentSet.value] || []);

onMounted(async () => {
  try {
    const allProducts = await loadProducts();
    const showcaseProducts = allProducts.filter(
      (product) => (product.available ?? true) && product.image && product.price !== null
    );

    products.value = shuffle(showcaseProducts);

    if (products.value.length === 0) {
      error.value = "Our showcase is taking a quick break. Please visit the shop to see everything.";
      return;
    }

    startAutoplay();
  } catch (err) {
    console.error(err);
    error.value = "Sorry, we couldn't load our shop highlights right now.";
  } finally {
    isLoading.value = false;
  }
});

onBeforeUnmount(stopAutoplay);

async function loadProducts() {
  try {
    const firstPage = await fetchProductPage(1);
    const totalPages = firstPage.pagination?.totalPages || 1;
    const remainingPages = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, index) => fetchProductPage(index + 2))
    );

    return [
      ...(firstPage.products || []),
      ...remainingPages.flatMap((page) => page.products || []),
    ];
  } catch (apiError) {
    if (process.env.NODE_ENV !== "development") {
      throw apiError;
    }

    const response = await fetch("/products.json");
    if (!response.ok) {
      throw apiError;
    }

    const data = await response.json();
    return data.products || [];
  }
}

async function fetchProductPage(page) {
  const response = await fetch(`/api/products?page=${page}&pageSize=50`);

  if (!response.ok) {
    throw new Error(`Failed to load products: ${response.status}`);
  }

  return response.json();
}

function shuffle(items) {
  const shuffled = [...items];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[index]];
  }

  return shuffled;
}

function nextSet() {
  if (productSets.value.length <= 1) {
    return;
  }

  currentSet.value = (currentSet.value + 1) % productSets.value.length;
  restartAutoplay();
}

function previousSet() {
  if (productSets.value.length <= 1) {
    return;
  }

  currentSet.value =
    (currentSet.value - 1 + productSets.value.length) % productSets.value.length;
  restartAutoplay();
}

function goToSet(index) {
  currentSet.value = index;
  restartAutoplay();
}

function toggleAutoplay() {
  isPaused.value = !isPaused.value;

  if (isPaused.value) {
    stopAutoplay();
  } else {
    startAutoplay();
  }
}

function pauseForInteraction() {
  stopAutoplay();
}

function resumeAfterInteraction() {
  if (!isPaused.value) {
    startAutoplay();
  }
}

function startAutoplay() {
  stopAutoplay();

  if (
    isPaused.value ||
    productSets.value.length <= 1 ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ) {
    return;
  }

  autoplayTimer = window.setInterval(() => {
    currentSet.value = (currentSet.value + 1) % productSets.value.length;
  }, AUTOPLAY_DELAY);
}

function stopAutoplay() {
  if (autoplayTimer) {
    window.clearInterval(autoplayTimer);
    autoplayTimer = undefined;
  }
}

function restartAutoplay() {
  if (!isPaused.value) {
    startAutoplay();
  }
}

function formatPrice(price, currency = "GBP") {
  const currencyCode = currency === "GB" ? "GBP" : currency;

  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currencyCode,
    }).format(price);
  } catch {
    return `£${Number(price).toFixed(2)}`;
  }
}

function displayImageUrl(url) {
  return centerCropCloudinaryUrl(url, { width: 900, height: 900 });
}
</script>

<template>
  <section id="about" class="about-section">
    <img src="../assets/bumblefox-logo.png" alt="Bumblefox UK" class="logo" />

    <div class="about-content">
      <p>
        Welcome to <strong>Bumblefox UK</strong>. We're a small family business
        printing fantastic art, gifts and ornaments from our favourite designers.
      </p>
    </div>
  </section>

  <section id="shop-highlights" class="shop-highlights">
    <div class="section-heading">
      <div>
        <p class="section-kicker">Fresh from the print bed</p>
        <h2>A little wander through our shop</h2>
      </div>
      <router-link to="/shop" class="view-shop-link">
        Shop all items
        <span aria-hidden="true">&rarr;</span>
      </router-link>
    </div>

    <p v-if="isLoading" class="status-message">Gathering a few favourites...</p>
    <div v-else-if="error" class="status-message">
      <p>{{ error }}</p>
      <router-link to="/shop">Browse the full shop</router-link>
    </div>

    <div
      v-else
      class="showcase"
      role="region"
      aria-roledescription="carousel"
      aria-label="Random shop highlights"
      @mouseenter="pauseForInteraction"
      @mouseleave="resumeAfterInteraction"
      @focusin="pauseForInteraction"
      @focusout="resumeAfterInteraction"
    >
      <div class="tile-stage" aria-live="polite">
        <Transition name="tile-swap" mode="out-in">
          <div :key="currentSet" class="tile-grid">
            <router-link
              v-for="(product, index) in visibleProducts"
              :key="product.id"
              :to="{ name: 'product', params: { id: product.id } }"
              class="product-tile"
              :class="`product-tile--${index + 1}`"
              :aria-label="`${product.title}, ${formatPrice(product.price, product.currency)}. View in shop.`"
            >
              <img
                :src="displayImageUrl(product.image)"
                :alt="product.title"
                class="product-image"
                loading="lazy"
              />
              <div class="tile-shade"></div>
              <div class="product-details">
                <span class="product-category">{{ product.category }}</span>
                <h3>{{ product.title }}</h3>
                <div class="product-meta">
                  <span v-if="product.brand">{{ product.brand }}</span>
                  <strong>{{ formatPrice(product.price, product.currency) }}</strong>
                </div>
              </div>
            </router-link>
          </div>
        </Transition>
      </div>

      <div v-if="productSets.length > 1" class="carousel-controls">
        <button type="button" class="round-control" aria-label="Previous products" @click="previousSet">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="m15 18-6-6 6-6" />
          </svg>
        </button>

        <div class="carousel-dots" aria-label="Choose product group">
          <button
            v-for="(_, index) in productSets"
            :key="index"
            type="button"
            class="carousel-dot"
            :class="{ active: currentSet === index }"
            :aria-label="`Show product group ${index + 1}`"
            :aria-current="currentSet === index ? 'true' : undefined"
            @click="goToSet(index)"
          ></button>
        </div>

        <button type="button" class="round-control" aria-label="Next products" @click="nextSet">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="m9 18 6-6-6-6" />
          </svg>
        </button>

        <button type="button" class="autoplay-control" @click="toggleAutoplay">
          {{ isPaused ? "Play" : "Pause" }}
        </button>
      </div>
    </div>
  </section>
</template>

<style scoped>
.about-section {
  background: #fff3e4;
  padding: 28px 20px 24px;
}

.logo {
  display: block;
  width: 128px;
  margin: 0 auto;
}

.about-content {
  max-width: 720px;
  margin: 0 auto;
  color: #45435d;
  font-size: 17px;
  line-height: 1.65;
  text-align: center;
}

.about-content p {
  margin: 8px 0 0;
}

.shop-highlights {
  padding: 56px 24px 64px;
  background: #fff;
}

.section-heading {
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 24px;
  max-width: 1180px;
  margin: 0 auto 24px;
  text-align: left;
}

.section-kicker {
  margin: 0 0 6px;
  color: #c65073;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.section-heading h2 {
  margin: 0;
  color: #2e2d61;
  font-size: 30px;
  line-height: 1.15;
}

.view-shop-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  padding: 10px 0;
  color: #2e2d61;
  font-weight: 800;
  text-decoration: none;
}

.view-shop-link:hover {
  text-decoration: underline;
}

.status-message {
  min-height: 240px;
  display: grid;
  place-content: center;
  gap: 8px;
  color: #5d5b70;
  text-align: center;
}

.showcase {
  max-width: 1180px;
  margin: 0 auto;
}

.tile-stage {
  position: relative;
  min-height: 514px;
}

.tile-grid {
  display: grid;
  grid-template-columns: 1.45fr 1fr 1fr;
  grid-template-rows: repeat(2, minmax(210px, 250px));
  gap: 14px;
}

.product-tile {
  position: relative;
  min-width: 0;
  overflow: hidden;
  border-radius: 8px;
  background: #f1edf0;
  color: #fff;
  text-decoration: none;
  isolation: isolate;
}

.product-tile--1 {
  grid-row: 1 / 3;
}

.product-image {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  transition: transform 0.45s ease;
}

.tile-shade {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(to top, rgba(25, 24, 50, 0.9), rgba(25, 24, 50, 0.04) 65%);
}

.product-details {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2;
  padding: 18px;
  text-align: left;
}

.product-category {
  display: inline-block;
  margin-bottom: 5px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.product-details h3 {
  margin: 0;
  color: #fff;
  font-size: 17px;
  line-height: 1.25;
}

.product-tile--1 .product-details h3 {
  font-size: 24px;
}

.product-meta {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
  font-size: 13px;
}

.product-meta span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.product-tile:hover .product-image,
.product-tile:focus-visible .product-image {
  transform: scale(1.045);
}

.product-tile:focus-visible,
.round-control:focus-visible,
.carousel-dot:focus-visible,
.autoplay-control:focus-visible {
  outline: 3px solid #e86a92;
  outline-offset: 3px;
}

.carousel-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 22px;
}

.round-control {
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  border: 1px solid #d7d3de;
  border-radius: 50%;
  background: #fff;
  color: #2e2d61;
  cursor: pointer;
}

.round-control:hover {
  background: #fff3e4;
}

.round-control svg {
  width: 20px;
  height: 20px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 2;
}

.carousel-dots {
  display: flex;
  align-items: center;
  gap: 7px;
  max-width: min(460px, 50vw);
  padding: 6px 2px;
  overflow-x: auto;
}

.carousel-dot {
  width: 7px;
  height: 7px;
  flex: 0 0 auto;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #c6c1cf;
  cursor: pointer;
  transition: width 0.2s ease, border-radius 0.2s ease, background-color 0.2s ease;
}

.carousel-dot.active {
  width: 24px;
  border-radius: 4px;
  background: #2e2d61;
}

.autoplay-control {
  min-width: 58px;
  padding: 7px 10px;
  border: 0;
  background: transparent;
  color: #5d5b70;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.tile-swap-enter-active,
.tile-swap-leave-active {
  transition: opacity 0.32s ease, transform 0.32s ease;
}

.tile-swap-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.tile-swap-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@media (max-width: 860px) {
  .tile-grid {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(3, 210px);
  }

  .tile-stage {
    min-height: 650px;
  }

  .product-tile--1 {
    grid-row: 1 / 3;
  }

  .product-tile--5 {
    grid-column: 1 / 3;
  }
}

@media (max-width: 600px) {
  .shop-highlights {
    padding: 40px 16px 48px;
  }

  .section-heading {
    align-items: flex-start;
  }

  .section-heading h2 {
    font-size: 25px;
  }

  .view-shop-link {
    font-size: 0;
  }

  .view-shop-link span {
    font-size: 24px;
  }

  .tile-stage {
    min-height: 370px;
  }

  .tile-grid {
    display: flex;
    gap: 10px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
  }

  .tile-stage::-webkit-scrollbar {
    display: none;
  }

  .product-tile {
    flex: 0 0 82%;
    min-height: 370px;
    scroll-snap-align: center;
  }

  .product-details h3,
  .product-tile--1 .product-details h3 {
    font-size: 20px;
  }

  .carousel-dots {
    max-width: 38vw;
  }
}

@media (prefers-reduced-motion: reduce) {
  .product-image,
  .carousel-dot,
  .tile-swap-enter-active,
  .tile-swap-leave-active {
    transition: none;
  }
}
</style>
